import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Mail, Phone, Instagram, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypes = [
  'Interior Design',
  'Architecture',
  'Turnkey',
  'Consultation',
  'Other'
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    project_type: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/inquiries`, formData);
      setSubmitted(true);
      toast.success('Thank you! We\'ll be in touch soon.');
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="contact-hero">
        <div className="max-w-4xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-dm-sans text-xs text-stone-400 tracking-wide uppercase mb-4 block"
          >
            Contact
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-stone-900 leading-tight mb-8"
          >
            Let's start a
            <br />
            <span className="italic">conversation</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-dm-sans text-lg text-stone-500 leading-relaxed max-w-2xl"
          >
            Whether you have a project in mind or simply want to learn more about our work, we'd love to hear from you.
          </motion.p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="pb-24 md:pb-32 lg:pb-40 px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-4"
          >
            <div className="space-y-12">
              {/* Address */}
              <div data-testid="contact-address">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin size={18} className="text-stone-400" />
                  <span className="font-manrope text-xs uppercase tracking-[0.2em] font-medium text-stone-900">
                    Studio
                  </span>
                </div>
                <p className="font-dm-sans text-stone-500 leading-relaxed">
                  511, Block D&E<br />
                  Chandigarh Citi Centre<br />
                  VIP Road, Zirakpur<br />
                  Punjab, India
                </p>
              </div>

              {/* Email */}
              <div data-testid="contact-email">
                <div className="flex items-center gap-3 mb-4">
                  <Mail size={18} className="text-stone-400" />
                  <span className="font-manrope text-xs uppercase tracking-[0.2em] font-medium text-stone-900">
                    Email
                  </span>
                </div>
                <a
                  href="mailto:mail.biomstudio@gmail.com"
                  className="font-dm-sans text-stone-500 hover:text-stone-900 transition-colors duration-300"
                >
                  mail.biomstudio@gmail.com
                </a>
              </div>

              {/* Phone */}
              <div data-testid="contact-phone">
                <div className="flex items-center gap-3 mb-4">
                  <Phone size={18} className="text-stone-400" />
                  <span className="font-manrope text-xs uppercase tracking-[0.2em] font-medium text-stone-900">
                    Phone
                  </span>
                </div>
                <a
                  href="tel:+917206235078"
                  className="font-dm-sans text-stone-500 hover:text-stone-900 transition-colors duration-300"
                >
                  +91-7206235078
                </a>
              </div>

              {/* Social */}
              <div data-testid="contact-social">
                <span className="font-manrope text-xs uppercase tracking-[0.2em] font-medium text-stone-900 mb-4 block">
                  Follow Us
                </span>
                <div className="flex gap-4">
                  <a
                    href="https://www.instagram.com/biom.studio?igsh=dnB5ZnhxZThtMm5y"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-social-instagram"
                    className="w-10 h-10 rounded-full border border-stone-200 flex items-center justify-center text-stone-400 hover:text-stone-900 hover:border-stone-900 transition-colors duration-300"
                    aria-label="Instagram"
                  >
                    <Instagram size={18} />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-8"
          >
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-stone-50 p-12 text-center"
                data-testid="form-success"
              >
                <CheckCircle size={48} className="text-stone-400 mx-auto mb-6" />
                <h3 className="font-manrope text-2xl font-light text-stone-900 mb-4">
                  Thank you for reaching out
                </h3>
                <p className="font-dm-sans text-stone-500">
                  We've received your message and will be in touch shortly.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Name */}
                  <div>
                    <label
                      htmlFor="name"
                      className="font-dm-sans text-xs text-stone-400 uppercase tracking-wide block mb-3"
                    >
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      data-testid="input-name"
                      placeholder="Your name"
                      className="w-full bg-transparent border-b border-stone-300 rounded-none px-0 py-4 font-dm-sans text-stone-900 placeholder:text-stone-400 focus:border-stone-900 focus:outline-none transition-colors duration-300"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="font-dm-sans text-xs text-stone-400 uppercase tracking-wide block mb-3"
                    >
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="input-email"
                      placeholder="your@email.com"
                      className="w-full bg-transparent border-b border-stone-300 rounded-none px-0 py-4 font-dm-sans text-stone-900 placeholder:text-stone-400 focus:border-stone-900 focus:outline-none transition-colors duration-300"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Phone */}
                  <div>
                    <label
                      htmlFor="phone"
                      className="font-dm-sans text-xs text-stone-400 uppercase tracking-wide block mb-3"
                    >
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="input-phone"
                      placeholder="+91 XXXXX XXXXX"
                      className="w-full bg-transparent border-b border-stone-300 rounded-none px-0 py-4 font-dm-sans text-stone-900 placeholder:text-stone-400 focus:border-stone-900 focus:outline-none transition-colors duration-300"
                    />
                  </div>

                  {/* Project Type */}
                  <div>
                    <label
                      htmlFor="project_type"
                      className="font-dm-sans text-xs text-stone-400 uppercase tracking-wide block mb-3"
                    >
                      Project Type *
                    </label>
                    <select
                      id="project_type"
                      name="project_type"
                      required
                      value={formData.project_type}
                      onChange={handleChange}
                      data-testid="input-project-type"
                      className="w-full bg-transparent border-b border-stone-300 rounded-none px-0 py-4 font-dm-sans text-stone-900 focus:border-stone-900 focus:outline-none transition-colors duration-300 appearance-none cursor-pointer"
                    >
                      <option value="" disabled>Select project type</option>
                      {projectTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="message"
                    className="font-dm-sans text-xs text-stone-400 uppercase tracking-wide block mb-3"
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    data-testid="input-message"
                    placeholder="Tell us about your project..."
                    className="w-full bg-transparent border-b border-stone-300 rounded-none px-0 py-4 font-dm-sans text-stone-900 placeholder:text-stone-400 focus:border-stone-900 focus:outline-none transition-colors duration-300 resize-none"
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-button"
                    className="inline-flex items-center gap-3 rounded-full px-10 py-4 text-xs uppercase tracking-widest font-manrope font-medium bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                    <Send size={16} />
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="bg-stone-100" data-testid="map-section">
        <div className="aspect-[21/9] bg-stone-200 flex items-center justify-center">
          <p className="font-dm-sans text-stone-400 text-sm">
            Map integration available upon request
          </p>
        </div>
      </section>
    </div>
  );
}
